import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, Variable } from 'src/api/types';
import { ScenarioCsvHeaders, ScenarioPackCsvHeaders } from 'src/api/types/misc';
import { ScenarioGroupWithDetails } from 'src/graphql';
import { formulaIdsToNames } from 'src/utils/formula';
import { formatDate, sanitizeForCSV } from 'src/utils/misc';
import { downloadCsvRows, sanitizeFilename } from 'src/utils/csvExport';

const getExportableFormula = (formula: string | null | undefined, variables: Variable[]) => {
  if (formula && formula.includes('#')) {
    return formulaIdsToNames(formula, variables);
  }

  return '';
};

type DownloadScenarioGroupCsv = (params: {
  group: Pick<
    ScenarioGroupWithDetails,
    'id' | 'name' | 'description' | 'author' | 'authorLogoPath' | 'category' | 'isSystem' | 'scenarios'
  >;
  workspaceVariables: Variable[];
}) => void;

const scenarioPackHeaders = [
  ScenarioPackCsvHeaders.PackID,
  ScenarioPackCsvHeaders.PackName,
  ScenarioPackCsvHeaders.PackDescription,
  ScenarioPackCsvHeaders.PackAuthor,
  ScenarioPackCsvHeaders.PackAuthorLogoPath,
  ScenarioPackCsvHeaders.PackCategory,
  ScenarioPackCsvHeaders.PackIsSystem,
  ScenarioCsvHeaders.Name,
  ScenarioPackCsvHeaders.ScenarioDescription,
  ScenarioCsvHeaders.ID,
  ScenarioCsvHeaders.Status,
  ScenarioCsvHeaders.Sources,
  ScenarioCsvHeaders.Events,
  ScenarioCsvHeaders.Consequences,
  ScenarioCsvHeaders.RiskOwner,
  ScenarioCsvHeaders.RiskNarrative,
  ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  ScenarioCsvHeaders.FrequencyNote,
  ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  ScenarioCsvHeaders.ConsequenceLowerFormula,
  ScenarioCsvHeaders.ConsequenceUpperFormula,
  ScenarioCsvHeaders.ConsequenceNote,
  ScenarioCsvHeaders.IdentifiedDate,
  ScenarioCsvHeaders.ReviewDate,
  ScenarioCsvHeaders.RiskManagementStrategy,
  ScenarioCsvHeaders.RiskManagementNote,
] as const;

export const downloadScenarioGroupCsv: DownloadScenarioGroupCsv = ({ group, workspaceVariables }) => {
  const scenarios = group.scenarios?.length ? group.scenarios : [null];
  const packIsSystem = group.isSystem == null ? '' : group.isSystem ? 'true' : 'false';

  const rows = [
    [...scenarioPackHeaders],
    ...scenarios.map((scenario) => [
      sanitizeForCSV(group.id),
      sanitizeForCSV(group.name),
      sanitizeForCSV(group.description),
      sanitizeForCSV(group.author),
      sanitizeForCSV(group.authorLogoPath),
      sanitizeForCSV(group.category),
      sanitizeForCSV(packIsSystem),
      sanitizeForCSV(scenario?.name),
      sanitizeForCSV(scenario?.description),
      sanitizeForCSV(scenario?.id),
      sanitizeForCSV(scenario?.status),
      sanitizeForCSV(getRiskSourcesName(scenario?.source)),
      sanitizeForCSV(getRiskEventsName(scenario?.event)),
      sanitizeForCSV(getRiskConsequencesName(scenario?.consequence)),
      sanitizeForCSV(scenario?.ownerName),
      sanitizeForCSV(scenario?.riskNarrative),
      sanitizeForCSV(scenario?.baseRisk?.frequencyTimes),
      sanitizeForCSV(scenario?.baseRisk?.frequencyYears),
      sanitizeForCSV(scenario?.frequencyNote),
      sanitizeForCSV(scenario?.baseRisk?.lower),
      sanitizeForCSV(scenario?.baseRisk?.upper),
      getExportableFormula(scenario?.lowerFormula, workspaceVariables),
      getExportableFormula(scenario?.upperFormula, workspaceVariables),
      sanitizeForCSV(scenario?.consequenceNote),
      formatDate(scenario?.riskIdentifiedDate),
      formatDate(scenario?.reviewDate),
      sanitizeForCSV(scenario?.managementStrategy),
      sanitizeForCSV(scenario?.managementNote),
    ]),
  ];

  downloadCsvRows({
    rows,
    filename: `${sanitizeFilename(group.name, 'Scenario_Pack')}_Scenario_Pack.csv`,
  });
};
